import styled from 'styled-components';
import { motion } from 'framer-motion';

// Base Container
export const PortfolioContainer = styled.div`
  background: var(--background-dark);
  color: var(--text-primary);
  min-height: 100vh;
  position: relative;
  width: 100%;
  overflow-x: hidden;
`;

// Navigation - Elegant Glass Morphism
export const Navigation = styled.nav`
  display: none; /* Hidden as requested */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--glass-border);
  box-shadow: var(--shadow-sm);
  padding: 1rem 4rem;
  z-index: 1000;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 1rem 2rem;
  }
`;

export const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  justify-content: center;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavItem = styled.li``;

export const NavLink = styled(motion.button)`
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  border: 2px solid ${props => props.active ? 'var(--primary-color)' : 'var(--border-color)'};
  color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  padding: 0.7rem 1.5rem;
  border-radius: var(--radius-full);
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'var(--glass-hover-bg)'};
    border-color: var(--primary-color);
    color: ${props => props.active ? 'white' : 'var(--primary-color)'};
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

export const MobileNavButton = styled(motion.button)`
  display: none;
  background: transparent;
  border: 2px solid var(--text-primary);
  color: var(--text-primary);
  padding: 0.7rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  margin-left: auto;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(88, 166, 255, 0.1);
    border-color: rgba(88, 166, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

export const MobileNav = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--glass-border);
  padding: 2rem;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: var(--shadow-lg);
  
  @media (min-width: 769px) {
    display: none;
  }
`;

// Hero Section - Minimal Clean with Maximum Impact
export const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8rem 10rem 10rem;
  position: relative;
  z-index: 1;
  background: var(--background-dark);
  overflow: hidden;
  
  @media (max-width: 1200px) {
    padding: 6rem 6rem 8rem;
  }
  
  @media (max-width: 1024px) {
    padding: 6rem 3rem 6rem;
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: 4rem 2rem 4rem;
  }
`;

export const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -2;
  overflow: hidden;
  
  /* Subtle noise texture for organic feel */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0.03;
    background-image: 
      repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.01) 35px, rgba(0,0,0,.01) 70px),
      repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(0,0,0,.01) 35px, rgba(0,0,0,.01) 70px);
  }
`;


export const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
  gap: 4rem;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

export const LeftContent = styled.div`
  max-width: 520px;
`;

export const BioText = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: var(--text-secondary);
  margin: 1rem 0;
  letter-spacing: -0.01em;
  
  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

export const AvatarContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 450px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
  overflow: hidden;
  background: linear-gradient(135deg, #E3F2FD 0%, #F0F8FF 100%);
  z-index: 2;
  transform: translateX(-5%);
  
  &:hover {
    box-shadow: var(--shadow-3d);
    transform: translateX(-5%) translateY(-5px);
    transition: all 0.4s ease;
  }
  
  @media (max-width: 768px) {
    height: 350px;
    transform: translateX(0);
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

export const InteractiveHint = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  opacity: 0.6;
  text-align: center;
  
  span {
    font-size: 1rem;
  }
`;

export const FeaturedProject = styled.div`
  flex: 1;
  max-width: 550px;
`;

export const Name = styled.h1`
  font-family: var(--font-display);
  font-size: 5.5rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  letter-spacing: -0.04em;
  line-height: 0.9;
  position: relative;
  z-index: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  
  /* Allow overlap with avatar */
  margin-right: -10%;
  
  @media (max-width: 1024px) {
    font-size: 4rem;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
    margin-right: 0;
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

export const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  margin: 1.5rem 0 3rem;
  color: var(--text-secondary);
  letter-spacing: 0.01em;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const Cursor = styled.span`
  display: inline-block;
  width: 3px;
  height: 2rem;
  background-color: var(--primary-color);
  margin-left: 2px;
  animation: blink 1s infinite;
  vertical-align: text-bottom;
  
  @keyframes blink {
    0%, 50% {
      opacity: 1;
    }
    51%, 100% {
      opacity: 0;
    }
  }
  
  @media (max-width: 768px) {
    height: 1.5rem;
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1rem 0 2rem 0;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

export const SocialLink = styled(motion.a)`
  color: var(--text-primary);
  text-decoration: none !important;
  padding: 0.7rem 1.5rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  font-weight: 500;
  font-size: 0.95rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  background: white;
  box-shadow: var(--shadow-sm);

  &:hover {
    text-decoration: none;
    background: var(--text-primary);
    border-color: var(--text-primary);
    color: white;
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

// Elegant 3D Quote Card
export const QuoteCard = styled(motion.div)`
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg);
  padding: 2.5rem;
  box-shadow: var(--shadow-3d);
  width: 100%;
  max-width: 600px;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  transform-style: preserve-3d;
  transform: perspective(1000px) rotateX(0deg) rotateY(0deg);
  
  /* Removed depth gradient overlay */
  
  &:hover {
    transform: perspective(1000px) rotateX(-2deg) rotateY(5deg) translateZ(20px);
    box-shadow: var(--shadow-3d), var(--shadow-ambient);
    border-color: var(--glass-hover-border);
  }
`;

export const QuoteContent = styled.div`
  margin: 0;
  flex: 1;
`;

export const QuoteText = styled.p`
  color: var(--silver);
  font-size: 1.05rem;
  line-height: 1.8;
  margin: 0;
  position: relative;
  z-index: 1;
  
  strong {
    color: var(--pearl);
    font-weight: 600;
    /* Removed gradient effect */
  }
  
  em {
    color: var(--primary-color);
    font-style: normal;
    font-weight: 500;
  }

  .tech {
    color: var(--accent-indigo);
    font-weight: 600;
    padding: 0 4px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--accent-indigo);
      opacity: 0.3;
    }
  }
  
  /* Removed hover gradient effect */
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(88, 166, 255, 0.2);
  margin-top: 1rem;
`;

export const OrganizationHeader = styled.div`
  margin-bottom: 1.5rem;
`;

export const OrganizationName = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  letter-spacing: -0.01em;
`;

export const RoleTitle = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
`;

// Enhanced Project Navigation
export const ProjectDots = styled.div`
  display: flex;
  gap: 0.7rem;
  align-items: center;
`;

export const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0;
  position: relative;
  background: transparent;
  color: ${props => props.active ? 'var(--primary-color)' : 'rgba(88, 166, 255, 0.4)'};
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.active ? 'var(--primary-color)' : 'rgba(88, 166, 255, 0.7)'};
    transform: scale(1.2);
  }
`;

export const ScrollButton = styled(motion.button)`
  position: absolute;
  bottom: 2rem;
  left: 0;
  right: 0;
  margin: 0 auto;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  opacity: 0.5;
  padding: 0.5rem 1rem;
  z-index: 10;
  transition: all var(--transition-base);
  width: fit-content;

  &:hover {
    opacity: 0.8;
    color: var(--text-secondary);
  }
`;

export const ScrollText = styled.span`
  font-size: 0.95rem;
  font-weight: 400;
  letter-spacing: 1.5px;
  color: inherit;
  opacity: 0.8;
  text-transform: uppercase;
`;

export const ViewProjectLink = styled(motion.a)`
  padding: 0.7rem 1.5rem;
  background: transparent;
  border: 1px solid rgba(88, 166, 255, 0.3);
  border-radius: var(--radius-xl);
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(88, 166, 255, 0.1);
    border-color: rgba(88, 166, 255, 0.5);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(88, 166, 255, 0.2);
  }
`;

// Repository Section - Depth & Atmosphere
export const RepositorySection = styled.section`
  padding: 6rem 6rem;
  position: relative;
  background: var(--background-middle);
  overflow: hidden;
  
  @media (max-width: 1200px) {
    padding: 8rem 4rem;
  }
  
  @media (max-width: 768px) {
    padding: 6rem 2rem;
  }
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 6rem;
  color: var(--text-primary);
  position: relative;
  letter-spacing: -0.03em;
  
  /* Removed accent gradient underline */
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 3rem;
  }
`;

export const SearchContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto 4rem;
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  max-width: 600px;
  min-width: 300px;
  padding: 1.2rem 2rem;
  border: 1px solid rgba(88, 166, 255, 0.2);
  border-radius: var(--radius-xl);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  color: var(--pearl);
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(88, 166, 255, 0.03);
    box-shadow: var(--shadow-md), var(--shadow-glow);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

export const RepositoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  margin: 0 auto;
  max-width: 1400px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const RepositoryCard = styled(motion.div)`
  background: white;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 2.5rem;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
  }
`;

export const RepoTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
  letter-spacing: -0.02em;
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: var(--accent-teal);
    }
  }
`;

export const RepoLanguage = styled.span`
  display: inline-block;
  padding: 0.5rem 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: 1.5rem 0 0 0;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

export const RepoStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  
  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }
`;

export const ProjectDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.6;
  margin: 1rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  min-height: 72px;
`;

// Back to Top Button
export const BackToTop = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 50px;
  height: 50px;
  background: var(--primary-color);
  border: none;
  border-radius: 50%;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  box-shadow: var(--shadow-lg);
  
  &:hover {
    opacity: 0.9;
  }
`;

// Utility Animations
export const ProjectIcon = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-bottom: 1rem;
`;

export const NavigationDots = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: center;
  margin-top: 2rem;
`;

export const NavDot = styled(motion.button)`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? 'var(--primary-color)' : 'rgba(88, 166, 255, 0.3)'};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'rgba(88, 166, 255, 0.5)'};
  }
`;

// Projects Section (if needed for additional projects)
export const ProjectsSection = styled.section`
  padding: 6rem 4rem;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 2rem;
  }
`;

export const ProjectsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ProjectShowcase = styled(motion.div)`
  flex: 1;
  position: relative;
  width: 100%;
  max-width: 600px;
  height: 400px;
  margin: 2rem;
  background: rgba(88, 166, 255, 0.05);
  border-radius: var(--radius-lg);
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(88, 166, 255, 0.1);
  
  @media (max-width: 1024px) {
    margin-top: 4rem;
  }
`;

export const ProjectImage = styled(motion.img)`
  width: 100%;
  height: 250px;
  object-fit: cover;
  border-radius: var(--radius-md);
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-lg);
`;